import React, { useEffect, useState } from 'react';
import TopBar from '../../components/TopBar';
import { 
  fetchAllBids, 
  createBid, 
  updateBid, 
  deleteBid, 
  fetchAllAuctions, 
  fetchAllAuctionsWithBranch,
  updateAuctionStatusDirect 
} from '../../lib/api';
import { toast } from 'react-toastify';
import { 
  FiTrendingUp, 
  FiDollarSign, 
  FiClock, 
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiUser,
  FiShoppingCart,
  FiPackage,
  FiCalendar,
  FiMapPin
} from 'react-icons/fi';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import { useAuth } from '../../contexts/AuthContext';

const Bids = () => {
  const { branchId, isAdmin } = useBranchFilter();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('live'); // 'live', 'coming', 'past'
  const [bids, setBids] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [auctionsWithBranch, setAuctionsWithBranch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [formData, setFormData] = useState({
    auction_id: '',
    auction_product_id: '',
    bidder: '',
    amount: '',
    is_auto: false
  });

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('Loading data...');
      const [bidsData, auctionsData, auctionsWithBranchData] = await Promise.all([
        fetchAllBids(),
        fetchAllAuctions(),
        fetchAllAuctionsWithBranch()
      ]);
      console.log('Loaded auctions with branch:', auctionsWithBranchData);
      
      // Filter auctions by branch for branch managers
      let filteredAuctions = auctionsWithBranchData;
      if (branchId) {
        filteredAuctions = auctionsWithBranchData.filter(a => a.branch_id === branchId);
        console.log(`Filtered to ${filteredAuctions.length} auctions for branch ${branchId}`);
      }
      
      setBids(bidsData);
      setAuctions(auctionsData);
      setAuctionsWithBranch(filteredAuctions);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load bids');
    } finally {
      setLoading(false);
    }
  };

  // Filter auctions based on status (or date as fallback)
  const filterAuctionsByDate = () => {
    console.log(`\n=== FILTERING FOR TAB: ${activeTab} ===`);
    console.log('Total auctions:', auctionsWithBranch.length);
    console.log('All auctions:', JSON.stringify(auctionsWithBranch, null, 2));
    
    const filtered = auctionsWithBranch.filter(auction => {
      console.log(`\n🔍 Checking Auction ${auction.id}:`);
      console.log('  Status:', auction.status);
      console.log('  Date:', auction.auction_date);
      console.log('  Active Tab:', activeTab);
      
      // ALWAYS use status-based filtering if status exists
      if (auction.status) {
        let match = false;
        if (activeTab === 'live') {
          match = auction.status === 'live';
          console.log('  ✓ Checking if status === "live":', match);
        } else if (activeTab === 'coming') {
          match = auction.status === 'scheduled';
          console.log('  ✓ Checking if status === "scheduled":', match);
        } else if (activeTab === 'past') {
          match = auction.status === 'completed';
          console.log('  ✓ Checking if status === "completed":', match);
        }
        console.log(`  → Final match result for ${activeTab}:`, match);
        return match;
      }
      
      // Fallback to date-based filtering only if NO status
      console.log('  ⚠️ No status field - using date filtering (fallback)');
      if (!auction.auction_date) {
        console.log('  ❌ No auction date either - excluding');
        return false;
      }
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const auctionDate = new Date(auction.auction_date);
      const auctionDay = new Date(auctionDate.getFullYear(), auctionDate.getMonth(), auctionDate.getDate());
      
      if (activeTab === 'live') {
        return auctionDay.getTime() === today.getTime();
      } else if (activeTab === 'coming') {
        return auctionDay.getTime() >= tomorrow.getTime();
      } else if (activeTab === 'past') {
        return auctionDay.getTime() < today.getTime();
      }
      return false;
    });
    
    console.log(`\n✅ Filtered result: ${filtered.length} auctions for ${activeTab} tab`);
    if (filtered.length > 0) {
      console.log('Filtered auctions:', filtered);
    }
    console.log('=== FILTERING COMPLETE ===\n');
    return filtered;
  };

  const filteredAuctions = filterAuctionsByDate();

  useEffect(() => {
    loadData();
  }, [branchId]); // Reload when branch changes

  const handleOpenModal = (bid = null) => {
    if (bid) {
      setEditingBid(bid);
      setFormData({
        auction_id: bid.auction_id || '',
        auction_product_id: bid.auction_product_id || '',
        bidder: bid.bidder || '',
        amount: bid.amount || '',
        is_auto: bid.is_auto || false
      });
    } else {
      setEditingBid(null);
      setFormData({
        auction_id: '',
        auction_product_id: '',
        bidder: '',
        amount: '',
        is_auto: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBid(null);
    setFormData({
      auction_id: '',
      auction_product_id: '',
      bidder: '',
      amount: '',
      is_auto: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bidder || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingBid) {
        await updateBid(editingBid.id, submitData);
        toast.success('Bid updated successfully');
      } else {
        await createBid(submitData);
        toast.success('Bid created successfully');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      console.error('Failed to save bid:', error);
      toast.error(`Failed to ${editingBid ? 'update' : 'create'} bid`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bid?')) {
      return;
    }

    try {
      await deleteBid(id);
      toast.success('Bid deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete bid:', error);
      toast.error('Failed to delete bid');
    }
  };

  const handleStatusChange = async (auctionId, newTabStatus) => {
    console.log('\n========================================');
    console.log('🔄 STATUS CHANGE INITIATED');
    console.log('========================================');
    console.log('Auction ID:', auctionId, '| Type:', typeof auctionId);
    console.log('New Tab Status:', newTabStatus);
    console.log('Current Active Tab:', activeTab);
    
    try {
      // Map tab selection to database status
      const statusMap = {
        'live': 'live',
        'coming': 'scheduled',
        'past': 'completed'
      };
      
      const dbStatus = statusMap[newTabStatus];
      console.log('📊 Database Status to set:', dbStatus);
      
      // Update in database
      console.log('💾 Calling database update...');
      const result = await updateAuctionStatusDirect(auctionId, dbStatus);
      console.log('✅ Update result:', result);
      
      if (!result) {
        console.error('❌ Update returned null - no rows were updated!');
        toast.error('Failed to update auction status - auction not found');
        return;
      }
      
      // Show success message
      const tabLabels = {
        'live': 'Live Bids',
        'coming': 'Coming Bids',
        'past': 'Past Bids'
      };
      toast.success(`Auction moved to ${tabLabels[newTabStatus]}!`);
      
      // Reload data to get fresh status from database
      console.log('🔄 Reloading data from database...');
      await loadData();
      console.log('📊 Data reloaded successfully!');
      console.log('Total auctions after reload:', auctionsWithBranch.length);
      
      // Switch to the target tab automatically
      console.log('🔀 Switching to tab:', newTabStatus);
      setActiveTab(newTabStatus);
      
      console.log('========================================');
      console.log('✅ STATUS CHANGE COMPLETE');
      console.log('========================================\n');
    } catch (error) {
      console.error('❌ Failed to update status:', error);
      toast.error('Failed to update auction status: ' + error.message);
    }
  };

  // Calculate statistics
  const totalBidAmount = bids.reduce((sum, bid) => sum + (parseFloat(bid.amount) || 0), 0);
  const autoBidsCount = bids.filter(bid => bid.is_auto).length;
  const manualBidsCount = bids.length - autoBidsCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <TopBar />
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar />
      <div className="p-6 pb-8">
        {/* Header */}
        <div className="mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              {isAdmin ? 'Bids Management' : `${user?.branch?.name || 'Branch'} - Bids Management`}
            </h1>
            <p className="text-gray-600 mt-1">
              {isAdmin ? 'Track and manage all auction bids' : 'Track and manage your branch auction bids'}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-full mr-4 ${
                activeTab === 'live' ? 'bg-green-100' : 
                activeTab === 'coming' ? 'bg-blue-100' : 
                'bg-orange-100'
              }`}>
                {activeTab === 'live' ? (
                  <FiClock className="text-green-600 text-2xl" />
                ) : activeTab === 'coming' ? (
                  <FiCalendar className="text-blue-600 text-2xl" />
                ) : (
                  <FiTrendingUp className="text-orange-600 text-2xl" />
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600 whitespace-nowrap">
                  {activeTab === 'live' ? 'Live Auctions' : activeTab === 'coming' ? 'Upcoming Auctions' : 'Past Auctions'}
                </p>
                <p className="text-2xl font-bold text-gray-800">{filteredAuctions.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 mr-4 flex-shrink-0">
                <FiDollarSign className="text-green-600 text-2xl" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600 whitespace-nowrap">Total Bids Amount</p>
                <p className="text-xl font-bold text-gray-800 truncate" title={`₹${totalBidAmount.toLocaleString()}`}>
                  ₹{totalBidAmount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 mr-4">
                <FiUser className="text-blue-600 text-2xl" />
              </div>
              <div>
                <p className="text-sm text-gray-600 whitespace-nowrap">Manual Bids</p>
                <p className="text-2xl font-bold text-gray-800">{manualBidsCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 mr-4">
                <FiClock className="text-purple-600 text-2xl" />
              </div>
              <div>
                <p className="text-sm text-gray-600 whitespace-nowrap">Auto Bids</p>
                <p className="text-2xl font-bold text-gray-800">{autoBidsCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-1">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('live')}
              className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'live'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <FiClock />
                <span>Live Bids</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('coming')}
              className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'coming'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <FiCalendar />
                <span>Coming Bids</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'past'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <FiTrendingUp />
                <span>Past Bids</span>
              </div>
            </button>
          </div>
        </div>

        {/* Auctions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auction ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auction Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Edit Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAuctions.map((auction) => (
                  <tr key={auction.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {auction.id ? (typeof auction.id === 'string' ? `${auction.id.substring(0, 8)}...` : auction.id) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900 font-medium">
                        <FiMapPin className="mr-2 text-blue-500" />
                        {auction.branch_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        {auction.branch_id ? (typeof auction.branch_id === 'string' ? `${auction.branch_id.substring(0, 8)}...` : auction.branch_id) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <FiCalendar className="mr-2 text-gray-400" />
                        {auction.auction_date ? new Date(auction.auction_date).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {activeTab === 'live' ? (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center w-fit">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                          Live Now
                        </span>
                      ) : activeTab === 'coming' ? (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          Upcoming
                        </span>
                      ) : (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          Completed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        onChange={(e) => handleStatusChange(auction.id, e.target.value)}
                        className="text-sm border-2 border-blue-600 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white hover:bg-blue-50 transition-all font-medium"
                        defaultValue=""
                      >
                        <option value="" disabled>Change Status...</option>
                        <option value="live">📍 Move to Live Bids</option>
                        <option value="coming">📅 Move to Coming Bids</option>
                        <option value="past">✅ Move to Past Bids</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => {
                            // View auction details - you can navigate or show more info
                            toast.info(`Viewing auction ${auction.id}`);
                          }}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="View Details"
                        >
                          <FiShoppingCart className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredAuctions.length === 0 && (
                  <tr>
                    <td className="px-6 py-12 text-center text-gray-500" colSpan="7">
                      <FiCalendar className="mx-auto text-4xl text-gray-300 mb-2" />
                      <p className="text-lg font-medium">
                        No {activeTab === 'live' ? 'live' : activeTab === 'coming' ? 'upcoming' : 'past'} auctions
                      </p>
                      <p className="text-sm">
                        {activeTab === 'live' && 'There are no auctions happening today'}
                        {activeTab === 'coming' && 'There are no scheduled auctions for the future'}
                        {activeTab === 'past' && 'No auctions have been completed yet'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredAuctions.length > 0 && (
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing {filteredAuctions.length} {activeTab} auction{filteredAuctions.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {editingBid ? 'Edit Bid' : 'Add New Bid'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Auction ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Auction ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.auction_id}
                    onChange={(e) => setFormData({ ...formData, auction_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter auction ID"
                  />
                </div>

                {/* Product ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.auction_product_id}
                    onChange={(e) => setFormData({ ...formData, auction_product_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter product ID"
                  />
                </div>

                {/* Bidder */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bidder ID *
                  </label>
                  <input
                    type="text"
                    value={formData.bidder}
                    onChange={(e) => setFormData({ ...formData, bidder: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter bidder ID"
                    required
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bid Amount (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter amount"
                    required
                  />
                </div>

                {/* Is Auto */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_auto"
                    checked={formData.is_auto}
                    onChange={(e) => setFormData({ ...formData, is_auto: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_auto" className="ml-2 text-sm text-gray-700">
                    Automatic Bid
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingBid ? 'Update' : 'Create'} Bid
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bids;

